import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useBatchedQuery } from 'bank-common-client';
import {
  Label as BaseLabel,
  Button,
  ProgressSpinner,
  ResizableTextarea,
  TextButton,
  fonts,
  mq,
  space,
} from 'folio-common-components';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import { useMediaLayout } from 'use-media';
import type { ReceiptItemFragment } from '../../common-client-queries.generated';
import type {
  LedgerCategoryIcon,
  LedgerCategoryItemRequirement,
} from '../../gqltypes';
import { useDragAndDrop } from '../../hooks/drag-and-drop-context';
import { PlusIcon, SmallArrowDownIcon } from '../../icons';
import { GetReceiptsForPaymentDocument } from '../../routes/payment/queries.generated';
import { isProbablyMobile } from '../../utils/device';
import { DefData, DefList, DefTitle } from '../definition-list';
import { CategorySuggestionSelector } from '../event-content/category-selector/CategorySuggestionSelector';
import { getLedgerIcon } from '../icon';
import { InfoBox } from '../info-box';
import { ReceiptRow } from '../receipt-view';
import { CompleteLabel, getCompletionStatus } from '../shared/bookkeeping';
import { LedgerCategoriesDocument } from '../shared/queries.generated';
import { StatusIndicator as BaseStatusIndicator } from '../status-indicator';
import { ReceiptMenu } from './bookkeeping-receipt-menu';
import { Uploader } from './uploader';

export type FieldState = 'notSet' | 'inProgress' | 'set' | 'error';
export type ReceiptState = 'default' | 'notNeeded' | 'lost';

export type Category = {
  title: string;
  fid: string;
  requirements: readonly LedgerCategoryItemRequirement[];
  icon: LedgerCategoryIcon;
};

export type AccountingSystemReference = {
  name: string;
  ledgerAccount: number | null;
};

const GrayText = styled.div`
  color: var(--muted-color);
`;

const InvalidReceiptBox: React.FC = () => {
  return (
    <InfoBox
      css={css`
        background: ${colors.yellowLight};
        ${space([16], 'margin-bottom')};
      `}
    >
      <strong>Obs!</strong> Kvitteringen er ugyldig eller uklar. Velg eventuelt
      «Trenger ikke» eller «Mistet» neste gang.
    </InfoBox>
  );
};

export type Props = {
  eventFid: string | undefined;
  showDefaultControlsAsRequired: boolean;
  showReceiptOptions: boolean;
  category: Category | null;
  categorySelectorLabel?: string;
  categorySuggestionFids: readonly string[];
  onSetCategory: (category: Category) => void;
  categoryState: FieldState;
  defaultCategorySelectorText: string;
  purpose: string | undefined;
  onSetPurpose: (purpose: string | undefined) => void;
  purposeState: FieldState;
  participants: string | undefined;
  onSetParticipants: (participants: string | undefined) => void;
  participantsState: FieldState;
  note: string | undefined;
  onSetNote: (note: string | undefined) => void;
  noteState: FieldState;
  receipts: readonly ReceiptItemFragment[];
  onSetReceiptFids: (receiptFids: readonly string[]) => void;
  receiptState: ReceiptState;
  receiptStateError: boolean;
  receiptIsNotNeededText: string | undefined;
  receiptIsNotNeededState: FieldState;
  onSetReceiptState: (state: ReceiptState, text?: string) => void;
  allRequirementsMet: boolean;
  hideCompleteLabel: boolean;
  editable: boolean;
  requirementsOverridden: boolean;
  accountingSystem?: AccountingSystemReference;
  hideRequirements?: boolean;
};

export const BookkeepingData: React.FC<Props> = props => {
  const {
    eventFid,
    category,
    showDefaultControlsAsRequired,
    showReceiptOptions,
    categorySelectorLabel,
    categorySuggestionFids,
    onSetCategory,
    categoryState,
    defaultCategorySelectorText,
    purpose,
    onSetPurpose,
    purposeState,
    participants,
    onSetParticipants,
    participantsState,
    note,
    onSetNote,
    noteState,
    onSetReceiptFids,
    receiptStateError,
    receiptState,
    receiptIsNotNeededText,
    receiptIsNotNeededState,
    onSetReceiptState,
    allRequirementsMet,
    requirementsOverridden,
    hideCompleteLabel,
    hideRequirements,
    editable,
    accountingSystem,
  } = props;
  const isWide = useMediaLayout(mq.large);
  const [hasTouchedReceipts, setHasTouchedReceipt] = React.useState(false);
  const [isUploadingReceipt, setIsUploadingReceipt] = React.useState(false);
  const [receiptFids, setReceiptFids] = React.useState(
    props.receipts.map(receipt => receipt.fid),
  );
  const [receiptUploadError, setReceiptUploadError] = React.useState(false);
  const [cachedReceipts, setCachedReceipts] = React.useState(props.receipts);
  const { currentlyUploadingNumber } = useDragAndDrop();

  const { data, loading: loadingReceipts } = useBatchedQuery(
    GetReceiptsForPaymentDocument,
    { variables: { receiptFids } },
  );

  // Cached receipts are rendered while new ones are fetched
  const receipts = data?.receipts ?? cachedReceipts;
  const allReceiptsAreQuestionable = receipts.every(e => !e.receiptLooksValid);

  React.useEffect(() => setCachedReceipts(receipts), [receipts]);

  React.useEffect(() => {
    setReceiptFids(props.receipts.map(receipt => receipt.fid));
  }, [props.receipts]);

  const handleReceiptUploadStart = React.useCallback(() => {
    setHasTouchedReceipt(true);
    setIsUploadingReceipt(true);
    setReceiptUploadError(false);
  }, []);

  const handleReceiptUploadError = React.useCallback(() => {
    setIsUploadingReceipt(false);
    setReceiptUploadError(true);
  }, []);

  const handleReceiptUploadSuccess = React.useCallback(
    (incomingReceiptFids: readonly string[]) => {
      const newReceiptFids = Array.from(
        new Set([...receiptFids, ...incomingReceiptFids]),
      );
      setReceiptFids(newReceiptFids);
      onSetReceiptFids(newReceiptFids);
      setIsUploadingReceipt(false);
    },
    [receiptFids, onSetReceiptFids],
  );

  const handleReceiptDelete = React.useCallback(
    (receipt: ReceiptItemFragment) => {
      setHasTouchedReceipt(true);
      const newReceiptFids = receiptFids.filter(fid => fid !== receipt.fid);
      setCachedReceipts(receipts =>
        receipts.filter(receipt => newReceiptFids.includes(receipt.fid)),
      );
      setReceiptFids(newReceiptFids);
      onSetReceiptFids(newReceiptFids);
    },
    [receiptFids, onSetReceiptFids],
  );

  const handleSetReceiptState = React.useCallback(
    (state: ReceiptState) => onSetReceiptState(state),
    [onSetReceiptState],
  );

  const hasReceipts = receipts.length > 0;
  const receiptIsDone = receiptState === 'default' ? hasReceipts : true;
  const receiptInProgress =
    currentlyUploadingNumber > 0 ||
    isUploadingReceipt ||
    (loadingReceipts &&
      // Until we have either uploaded or deleted a receipt, we don't want to show a spinner.
      // This avoids spinning when opening the transaction row the first time.
      hasTouchedReceipts);

  const completionStatus = getCompletionStatus({
    allRequirementsMet,
    overridden: requirementsOverridden,
  });

  const receiptHeading = getReceiptHeadingText(receiptState);
  const receiptSection = (
    <ReceiptGrid>
      <Receipts
        eventFid={eventFid}
        receipts={receipts}
        uploadInProgress={receiptInProgress}
        onDeleteReceipt={handleReceiptDelete}
        editable={editable}
      />
    </ReceiptGrid>
  );
  const isAddedByAccountingSystem = accountingSystem != null;

  return (
    <>
      <BookkeepingGrid>
        <div
          css={css`
            display: grid;
            ${space([32], 'gap')};
            grid-auto-rows: max-content;
          `}
        >
          {isAddedByAccountingSystem && (
            <BookkeepingGrid>
              {accountingSystem.ledgerAccount ? (
                <DefList>
                  <DefTitle>Bokført på</DefTitle>
                  <DefData>
                    Konto {accountingSystem.ledgerAccount} i{' '}
                    {accountingSystem.name}
                  </DefData>
                </DefList>
              ) : (
                <DefList>
                  <DefTitle>
                    Dette kjøpet er bokført i {accountingSystem.name}
                  </DefTitle>
                </DefList>
              )}
            </BookkeepingGrid>
          )}
          {!hideRequirements && (
            <>
              <div>
                <CategorySelector
                  label={
                    categorySelectorLabel ?? 'Kategori til regnskapet ditt'
                  }
                  defaultText={defaultCategorySelectorText}
                  category={category}
                  buttonType={
                    showDefaultControlsAsRequired ? 'primary' : 'secondary'
                  }
                  suggestionFids={categorySuggestionFids}
                  state={categoryState}
                  onChange={category => {
                    onSetCategory(category);
                  }}
                  editable={editable}
                />
              </div>
              <div>
                <Label
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  Kvittering eller faktura{' '}
                  {receiptIsDone ? <StatusIndicator state="set" /> : null}
                </Label>
                {editable ? (
                  <>
                    {hasReceipts ? (
                      <>
                        {allReceiptsAreQuestionable ? (
                          <InvalidReceiptBox />
                        ) : null}
                        <div
                          css={css`
                            display: flex;
                          `}
                        >
                          <Uploader
                            eventFid={eventFid}
                            buttonType="text"
                            handleReceiptUploadStart={handleReceiptUploadStart}
                            handleReceiptUploadError={handleReceiptUploadError}
                            handleReceiptUploadSuccess={
                              handleReceiptUploadSuccess
                            }
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        {receiptHeading ? (
                          <span
                            css={css`
                              margin-right: 8px;
                            `}
                          >
                            {receiptHeading}{' '}
                          </span>
                        ) : null}
                        {receiptState === 'default' && (
                          <Uploader
                            eventFid={eventFid}
                            buttonType={
                              category?.requirements.includes('Receipt') ||
                              showDefaultControlsAsRequired
                                ? 'primary'
                                : 'secondary'
                            }
                            handleReceiptUploadStart={handleReceiptUploadStart}
                            handleReceiptUploadError={handleReceiptUploadError}
                            handleReceiptUploadSuccess={
                              handleReceiptUploadSuccess
                            }
                          />
                        )}
                        {showReceiptOptions ? (
                          <>
                            {/* For some reason, a normal space does not produce a space in Safari */}
                            &nbsp;
                            <ReceiptMenu
                              buttonType={
                                receiptState === 'default'
                                  ? 'secondary'
                                  : 'text'
                              }
                              state={receiptState}
                              onSetReceiptState={handleSetReceiptState}
                            />
                          </>
                        ) : null}
                      </>
                    )}
                    {(receiptUploadError || receiptStateError) && (
                      <CouldNotSaveErrorMessage />
                    )}
                  </>
                ) : (
                  <GrayText>
                    {hasReceipts
                      ? 'Kvittering lagt til'
                      : receiptHeading || 'Ikke lagt til'}
                  </GrayText>
                )}
              </div>
              {!isWide && (hasReceipts || receiptInProgress) && (
                <div>{receiptSection}</div>
              )}
              {receiptState === 'notNeeded' && (
                <div>
                  <RequirementInput
                    label="Hvorfor trengs ikke kvittering?" // FIXME
                    buttonAriaLabel="Legg til eller endre forklaring"
                    value={receiptIsNotNeededText}
                    state={receiptIsNotNeededState}
                    onSave={value => onSetReceiptState('notNeeded', value)}
                    editable={editable}
                  />
                </div>
              )}
              {category?.requirements.includes('Purpose') && (
                <div>
                  <RequirementInput
                    label="Hva var formålet?"
                    buttonAriaLabel="Legg til eller endre formål"
                    value={purpose}
                    state={purposeState}
                    onSave={onSetPurpose}
                    editable={editable}
                  />
                </div>
              )}
              {category?.requirements.includes('Participants') && (
                <div>
                  <RequirementInput
                    label="Hvem var med?"
                    buttonAriaLabel="Legg til eller endre deltakere"
                    value={participants}
                    state={participantsState}
                    onSave={onSetParticipants}
                    editable={editable}
                  />
                </div>
              )}
              <div>
                <Note
                  value={note}
                  onSave={onSetNote}
                  state={noteState}
                  editable={editable}
                />
              </div>
              {!editable && !accountingSystem?.name && (
                <GrayText>Du har ikke tilgang til å gjøre endringer</GrayText>
              )}
              {isWide && !hideCompleteLabel && (
                <div>
                  <CompleteLabel status={completionStatus} />
                </div>
              )}
            </>
          )}
        </div>
        {isWide && <div>{receiptSection}</div>}
      </BookkeepingGrid>
      {!isWide && !hideCompleteLabel && (
        <div
          css={css`
            margin: 12px 0;
          `}
        >
          <CompleteLabel status={completionStatus} />
        </div>
      )}
    </>
  );
};

const CategorySelector: React.FC<{
  label: string;
  defaultText: string;
  category: Category | null;
  buttonType: 'primary' | 'secondary';
  suggestionFids: readonly string[];
  state: FieldState;
  onChange: (category: Category) => void;
  editable: boolean;
}> = ({
  label,
  defaultText,
  category,
  buttonType,
  suggestionFids,
  state,
  onChange,
  editable,
}) => {
  const { loading: loadingCategories } = useBatchedQuery(
    LedgerCategoriesDocument,
  );
  const [hasOpenCategorySelector, setHasOpenCategorySelector] =
    React.useState(false);
  const [justClosed, setJustClosed] = React.useState(false);
  const categoryButtonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (justClosed) {
      // When choosing something from the dropdown menu with Enter,
      // the menu is closed, and the category selector is focused.
      // The key event (Enter) is then dispatched on the category
      // selector again, which opens the menu.
      // This may be a React issue, where it dispatches a fake event.
      // The timeout works around the issue.
      window.setTimeout(() => {
        categoryButtonRef.current?.focus({ preventScroll: true });
        setJustClosed(false);
      }, 0);
    }
  }, [justClosed]);

  return hasOpenCategorySelector ? (
    <>
      <div
        css={css`
          text-align: right;
        `}
      >
        <TextButton
          onClick={() => {
            setHasOpenCategorySelector(false);
            setJustClosed(true);
          }}
          // On non-mobile, the input is focused instead
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={isProbablyMobile}
        >
          Avbryt
        </TextButton>
      </div>
      <div
        css={css`
          margin-top: 12px;
        `}
      >
        {loadingCategories ? (
          <ProgressSpinner size={48} />
        ) : (
          <CategorySuggestionSelector
            loading={false}
            onSubcategorySelected={category => {
              setHasOpenCategorySelector(false);
              onChange(category);
              setJustClosed(true);
            }}
            selectedSubcategoryFid={category?.fid ?? null}
            suggestionFids={suggestionFids}
          />
        )}
      </div>
    </>
  ) : (
    <>
      <Label
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        {label} <StatusIndicator state={state} />
      </Label>
      <Button
        data-testid="category-selector"
        aria-label={category ? `${label}: ${category.title}` : undefined}
        ref={categoryButtonRef}
        size="large"
        level={buttonType}
        fullWidth
        disabled={!editable}
        onClick={() => {
          setHasOpenCategorySelector(true);
        }}
        css={buttonType === 'primary' ? fonts.font200medium : fonts.font200book}
      >
        {category ? (
          <span
            css={css`
              display: grid;
              grid-template-columns: auto 1fr auto;
              gap: 16px;
              width: 100%;
              align-items: center;
              text-align: left;
            `}
          >
            {getLedgerIcon(category.icon)}
            {category.title}
            <SmallArrowDownIcon />
          </span>
        ) : (
          <span
            css={css`
              display: grid;
              grid-template-columns: 1fr auto;
              gap: 16px;
              width: 100%;
              align-items: center;
              text-align: left;
            `}
          >
            {defaultText}
            <SmallArrowDownIcon />
          </span>
        )}
      </Button>
      {state === 'error' && <CouldNotSaveErrorMessage />}
    </>
  );
};

const AnnotationValue = styled.span`
  ${space([16], 'margin-right')};
  word-break: break-word;
`;

const RequirementInput: React.FC<{
  value: string | undefined;
  label: string;
  buttonAriaLabel: string;
  state: FieldState;
  onSave: (value: string | undefined) => void;
  editable: boolean;
}> = ({ value, label, buttonAriaLabel, state, onSave, editable }) => {
  const [id] = React.useState(String(Math.random()));
  const [isOpen, setIsOpen] = React.useState(false);
  const [justOpened, setJustOpened] = React.useState(false);
  const [justClosed, setJustClosed] = React.useState(false);
  const [lastSavedValue, setLastSavedValue] = React.useState(value);
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (justOpened) {
      if (textAreaRef.current) {
        textAreaRef.current.focus({ preventScroll: true });
        textAreaRef.current.selectionStart = textAreaRef.current.selectionEnd =
          textAreaRef.current.value.length;
      }
      setJustOpened(false);
    }
  }, [justOpened]);

  React.useEffect(() => {
    if (justClosed) {
      buttonRef.current?.focus({ preventScroll: true });
      setJustClosed(false);
    }
  }, [justClosed]);

  React.useLayoutEffect(() => {
    if (state === 'error') {
      setIsOpen(true);
    }
  }, [state]);

  const collapsedContent = value ? (
    <>
      <AnnotationValue>{value}</AnnotationValue>{' '}
      {editable ? (
        <TextButton
          ref={buttonRef}
          aria-label={buttonAriaLabel}
          onClick={() => {
            setIsOpen(true);
            setJustOpened(true);
          }}
        >
          Endre
        </TextButton>
      ) : null}
    </>
  ) : editable ? (
    <Button
      ref={buttonRef}
      aria-label={buttonAriaLabel}
      icon={<PlusIcon />}
      onClick={() => {
        setIsOpen(true);
        setJustOpened(true);
      }}
    >
      Legg til
    </Button>
  ) : (
    <GrayText>Ikke lagt til</GrayText>
  );
  const expandedContent = (
    <TextArea
      ref={textAreaRef}
      id={id}
      value={state === 'error' ? lastSavedValue : value}
      onSave={value => {
        setLastSavedValue(value);
        onSave(value);
        setIsOpen(false);
        setJustClosed(true);
      }}
      disabled={!editable}
    />
  );

  return (
    <>
      <Label
        for={id}
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        {label} <StatusIndicator state={state} />
      </Label>
      <div>{isOpen ? expandedContent : collapsedContent}</div>
      {state === 'error' && <CouldNotSaveErrorMessage />}
    </>
  );
};

const Note: React.FC<{
  value: string | undefined;
  state: FieldState;
  onSave: (note: string | undefined) => void;
  editable: boolean;
}> = ({ value, state, onSave, editable }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [justOpened, setJustOpened] = React.useState(false);
  const [justClosed, setJustClosed] = React.useState(false);
  const [lastSavedValue, setLastSavedValue] = React.useState(value);
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (justOpened) {
      if (textAreaRef.current) {
        textAreaRef.current.focus({ preventScroll: true });
        textAreaRef.current.selectionStart = textAreaRef.current.selectionEnd =
          textAreaRef.current.value.length;
      }
      setJustOpened(false);
    }
  }, [justOpened]);

  React.useEffect(() => {
    if (justClosed) {
      buttonRef.current?.focus({ preventScroll: true });
      setJustClosed(false);
    }
  }, [justClosed]);

  React.useLayoutEffect(() => {
    if (state === 'error') {
      setIsOpen(true);
    }
  }, [state]);

  const collapsedContent = value ? (
    <>
      <AnnotationValue>{value}</AnnotationValue>{' '}
      {editable ? (
        <TextButton
          ref={buttonRef}
          aria-label="Endre notat"
          onClick={() => {
            setJustOpened(true);
            setIsOpen(true);
          }}
        >
          Endre
        </TextButton>
      ) : null}
    </>
  ) : editable ? (
    <span
      css={css`
        display: flex;
        align-items: center;
      `}
    >
      <TextButton
        ref={buttonRef}
        onClick={() => {
          setJustOpened(true);
          setIsOpen(true);
        }}
      >
        Legg til et notat
      </TextButton>{' '}
      <StatusIndicator state={state} />
    </span>
  ) : (
    <GrayText>Ikke lagt til</GrayText>
  );
  const expandedContent = (
    <TextArea
      ref={textAreaRef}
      id="note"
      value={state === 'error' ? lastSavedValue : value}
      onSave={value => {
        setLastSavedValue(value);
        onSave(value);
        setIsOpen(false);
        setJustClosed(true);
      }}
      disabled={!editable}
    />
  );

  return (
    <>
      {(isOpen || value || !editable) && (
        <Label
          for="note"
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          Notat <StatusIndicator state={state} />
        </Label>
      )}
      <div>{isOpen ? expandedContent : collapsedContent}</div>
      {state === 'error' && <CouldNotSaveErrorMessage />}
    </>
  );
};

const TextAreaWithRef: React.ForwardRefRenderFunction<
  HTMLTextAreaElement,
  {
    id: string;
    value: string | undefined;
    onSave: (value: string | undefined) => void;
    disabled: boolean;
  }
> = ({ id, value: initialValue, onSave, disabled }, ref) => {
  const [value, setValue] = React.useState(initialValue);
  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <ResizableTextarea
        id={id}
        ref={ref}
        onChange={setValue}
        value={value || ''}
        maxLength={1000}
        css={css`
          display: block;
          width: 100%;
          padding-bottom: 64px;
          min-height: 150px;
        `}
        onKeyDown={event => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            onSave(value?.trim());
            event.preventDefault();
          }
        }}
        disabled={disabled}
      />
      <Button
        css={css`
          position: absolute;
          bottom: 8px;
          left: 8px;
        `}
        onClick={() => {
          onSave(value?.trim());
        }}
      >
        Lagre
      </Button>
    </div>
  );
};

const TextArea = React.forwardRef(TextAreaWithRef);

const Receipts: React.FC<{
  eventFid: string | undefined;
  receipts: readonly ReceiptItemFragment[];
  uploadInProgress: boolean;
  editable: boolean;
  onDeleteReceipt: (receipt: ReceiptItemFragment) => void;
}> = ({ eventFid, receipts, uploadInProgress, editable, onDeleteReceipt }) => {
  return (
    <>
      {receipts.length > 0 ? (
        Array.from(receipts)
          .sort((l, r) => l.uploadedAt.localeCompare(r.uploadedAt))
          .map((receipt, i) => (
            <ReceiptRow
              eventFid={eventFid}
              imageNr={i + 1}
              key={receipt.fid}
              receipt={receipt}
              data-testid="receipt-thumbnail"
              onDelete={() => onDeleteReceipt(receipt)}
              editable={editable}
            />
          ))
      ) : !editable ? (
        <ReceiptPlaceholder backgroundColor={colors.grayAiryOpaque}>
          <div
            css={css`
              position: absolute;
            `}
          >
            Ingen kvittering
          </div>
        </ReceiptPlaceholder>
      ) : (
        !uploadInProgress && (
          <ReceiptPlaceholder backgroundColor={colors.grayAiryOpaque}>
            <div
              css={css`
                position: absolute;
              `}
            >
              Dra en fil hit
            </div>
          </ReceiptPlaceholder>
        )
      )}

      {uploadInProgress && (
        <ReceiptPlaceholder backgroundColor={colors.grayAiryOpaque}>
          <div
            css={css`
              position: absolute;
            `}
          >
            <ProgressSpinner size={48} />
          </div>
        </ReceiptPlaceholder>
      )}
    </>
  );
};

const CouldNotSaveErrorMessage: React.FC = () => (
  <p
    css={css`
      color: ${colors.wcagRed};
      ${space([8], 'margin-bottom')};
      ${space(
        [4],
        'margin-top',
      )}; /* Label has 4px bottom padding, so use 4 here */
    `}
  >
    Ville ikke lagre. Prøv igjen.
  </p>
);

const StatusIndicator: React.FC<{ state: FieldState }> = ({ state }) => (
  <BaseStatusIndicator
    css={css`
      margin-left: 8px;
    `}
    state={
      state === 'set'
        ? 'done'
        : state === 'inProgress'
        ? 'inProgress'
        : 'hidden'
    }
  />
);

export const BookkeepingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  ${space([48], 'gap')};
`;

export const ReceiptGrid = styled.div`
  display: grid;
  ${space([8], 'gap')};
`;

const Label = styled(BaseLabel)`
  padding-bottom: 12px;
`;

export const ReceiptPlaceholder = styled.div<{
  backgroundColor: string | null;
}>`
  position: relative;
  border-radius: 8px;
  background: ${({ backgroundColor }) =>
    backgroundColor ? backgroundColor : colors.background};
  padding-top: 100%;
  display: grid;
  place-items: center;
`;

function getReceiptHeadingText(state: ReceiptState) {
  if (state === 'notNeeded') {
    return 'Kvittering trengs ikke';
  }

  if (state === 'lost') {
    return 'Kvittering er mistet';
  }

  return undefined;
}
